import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useScrollSnap } from '../hooks/useScrollSnap';
import {
  IonContent,
  IonButton,
  IonIcon,
  IonList,
  IonItem,
  IonLabel,
  IonBadge,
  IonCard,
  IonCardContent,
  IonRow,
  IonCol,
  IonRefresherContent,
  IonText,
  IonGrid,
  IonSkeletonText,
  IonSearchbar,
  IonPopover,
  IonModal,
  IonFab,
  IonAvatar,
  IonSpinner,
  IonPage,
  useIonRouter,
} from '@ionic/react';
import { Keyboard } from '@capacitor/keyboard';
import { KEYBOARD_GAP_PX } from '../hooks/useKeyboardHeight';
import { close, calendar, time, search, swapVertical, closeOutline, chevronBack, sparklesOutline, locationOutline, receiptOutline, chevronDownOutline, funnelOutline, listOutline, calendarOutline, personOutline } from 'ionicons/icons';
import styles from './BookingsListModal.module.scss';
import { getDefaultAvatar, proxyImage } from '@rsmaids/shared/utils/avatarUtils';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { DataStorageService } from '@rsmaids/shared/services/DataStorageService';
import { BookingItem } from '@rsmaids/shared/types/bookings';
import { getBookingsFromCache } from '@rsmaids/shared/services/api/bookingsCacheService';
import BasePageWithHeader, { TitleCard } from './BasePageWithHeader';
import PortalDropdown from './PortalDropdown';
import CreateInvoice from '../pages/CreateInvoice';
import PdfViewerMobile from './PdfViewerMobile';
import { getBookingPaymentStatusBatch, getZohoInvoices } from '../services/zohoInvoicingService';
import { getServiceOrder } from '../services/zohoServiceOrderService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import PositionedRefresher from './PositionedRefresher';
import { applySearchOverlayPosition } from '../utils/overlayPosition';
import { useTranslation } from 'react-i18next';
import { translateDynamic } from '../i18n/dynamic';
import { intlLocale } from '../i18n/locale';

interface BookingsListModalProps {
  onClose?: () => void;
  // Lets a caller (e.g. Bookings.tsx's overview stat tiles) open the list
  // pre-filtered — same activeFilter/filterValue pair the in-modal filter
  // menu itself sets, just seeded on mount instead of defaulting to 'all'.
  initialActiveFilter?: string;
  initialFilterValue?: string;
  // Render as a routed page (<IonPage>) instead of modal content. The caller
  // supplies the <IonModal> wrapper in modal mode; in page mode this component
  // wraps itself and closes via router.goBack().
  asPage?: boolean;
}

const BookingsListModal: React.FC<BookingsListModalProps> = ({ onClose, initialActiveFilter, initialFilterValue, asPage = false }) => {
  const { t: tr } = useTranslation();
  const router = useIonRouter();
  const handleClose = useCallback(() => {
    if (onClose) onClose();
    else if (router.canGoBack()) router.goBack();
    else router.push('/tabs/dashboard');
  }, [onClose, router]);
  const [searchText, setSearchText] = useState('');
  const [bookings, setBookings] = useState<BookingItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<string>(initialActiveFilter ?? 'all');
  const [filterValue, setFilterValue] = useState<string>(initialFilterValue ?? 'all');
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [isSearchClosing, setIsSearchClosing] = useState(false);
  const [isSearchHidden, setIsSearchHidden] = useState(false);
  const [sortBy, setSortBy] = useState<'date-newest' | 'date-oldest' | 'name-az' | 'name-za'>('date-newest');
  const [showCustomDatePopover, setShowCustomDatePopover] = useState(false);
  const [customStartDate, setCustomStartDate] = useState<string | undefined>(undefined);
  const [customEndDate, setCustomEndDate] = useState<string | undefined>(undefined);
  const titleCardRef = useRef<HTMLIonRowElement>(null);
  const ionContentRef = useRef<HTMLIonContentElement | null>(null);
  const scrollContainerRef = useRef<HTMLIonGridElement>(null);
  const overlayRef = useRef<HTMLElement | null>(null);
  const searchRef = useRef<HTMLIonSearchbarElement | null>(null);
  const [showFilterCatMenu, setShowFilterCatMenu] = useState(false);
  const [showFilterValMenu, setShowFilterValMenu] = useState(false);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const filterCatTriggerRef = useRef<HTMLButtonElement>(null);
  const filterValTriggerRef = useRef<HTMLButtonElement>(null);
  const sortTriggerRef = useRef<HTMLButtonElement>(null);
  const pickerPortalRef = useRef<HTMLDivElement>(null);
  const rafTick = useRef(false);
  const [refresherDisabled, setRefresherDisabled] = useState(false);
  const wasAtTop = useRef(true);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isLoadingMoreRef = useRef(false);
  const [visibleCount, setVisibleCount] = useState(30);
  const [paymentOverrides, setPaymentOverrides] = useState<Record<string, { paid: boolean; paymentStatus: string }>>({});
  const fetchedPaymentIds = useRef<Set<string>>(new Set());
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfDataUrl, setPdfDataUrl] = useState<string | null>(null);
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [pdfModalTitle, setPdfModalTitle] = useState('');
  const [isLookingUpInvoice, setIsLookingUpInvoice] = useState<string | null>(null);
  const [isPdfFullscreen, setIsPdfFullscreen] = useState(false);

  const PAGE_DAYS = 30;
  // Track loaded window as refs so loadMore never has stale closure issues
  const loadedStartRef = useRef<Date>((() => { const d = new Date(); d.setDate(d.getDate() - PAGE_DAYS); return d; })());
  // Wix paging state, used once scrolling back leaves the Firestore window
  const wixModeRef = useRef(false);
  const wixBeforeRef = useRef<Date>(new Date());
  const wixCursorRef = useRef<string | undefined>(undefined);
  const loadedEndRef = useRef<Date>((() => { const d = new Date(); d.setDate(d.getDate() + PAGE_DAYS); return d; })());

  const fetchBookings = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const start = loadedStartRef.current;
      const end = loadedEndRef.current;

      // Firestore is the primary source — has booking data + zohoPaid/zohoPaymentStatus
      const cached = await getBookingsFromCache(start, end);
      if (cached.length > 0) {
        setBookings(cached);
        setHasMore(true);
        return;
      }

      // Firestore empty — fall back to Wix API (booking data only, no payment status)
      const dataService = DataServiceFactory.getService();
      if (!dataService) throw new Error('DataService not available');
      const fetched = await dataService.bookings.getBookingsAndBlockedSlotsByDateRange(start, end, []);
      if (!Array.isArray(fetched)) throw new Error('Invalid bookings response');
      setBookings(fetched as BookingItem[]);
      setHasMore(true);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(tr('components.BookingsListModal.failedToLoadBookingsPleaseTry'));
      setBookings([]);
    } finally {
      setIsLoading(false);
    }
  }, [tr]);

  // Fetch bookings on component mount
  useEffect(() => {
    Promise.resolve().then(() => fetchBookings());
  }, [fetchBookings]);

  const loadMore = useCallback(async () => {
    console.log('[loadMore] called — hasMore:', hasMore, 'isLoadingMore:', isLoadingMoreRef.current);
    if (isLoadingMoreRef.current || !hasMore) return;
    isLoadingMoreRef.current = true;
    setIsLoadingMore(true);

    const newStart = new Date(loadedStartRef.current);
    newStart.setDate(newStart.getDate() - PAGE_DAYS);
    const newEnd = new Date(loadedStartRef.current);
    console.log('[loadMore] fetching range:', newStart.toISOString(), '→', newEnd.toISOString());

    try {
      let more: BookingItem[] = [];

      // Firestore covers its own window, 30 days per page. Once it comes back
      // sparse we're outside that window and switch to Wix for good.
      if (!wixModeRef.current) {
        try {
          more = await getBookingsFromCache(newStart, newEnd);
          console.log('[loadMore] Firestore returned:', more.length);
        } catch (e) { console.warn('[loadMore] Firestore error:', e); }
        if (more.length < 10) {
          wixModeRef.current = true;
          wixBeforeRef.current = newEnd;
          wixCursorRef.current = undefined;
        }
      }

      // Outside the Firestore window: Wix, 30 bookings per page, newest first,
      // continuing from the previous page's cursor until Wix has no more.
      let wixHasNext = true;
      if (wixModeRef.current) {
        const bookingsApi = DataServiceFactory.getService()?.bookings as unknown as {
          getBookingsPageBefore?: (before: Date, cursor?: string, limit?: number) =>
            Promise<{ bookings: BookingItem[]; nextCursor?: string; hasNext: boolean }>;
        } | undefined;
        if (bookingsApi?.getBookingsPageBefore) {
          try {
            const page = await bookingsApi.getBookingsPageBefore(wixBeforeRef.current, wixCursorRef.current, 30);
            console.log('[loadMore] Wix page:', page.bookings.length, 'hasNext:', page.hasNext);
            wixCursorRef.current = page.nextCursor;
            wixHasNext = page.hasNext;
            const have = new Set(more.map(b => b.id));
            more = [...more, ...page.bookings.filter(b => !have.has(b.id))];
          } catch (e) { console.error('[loadMore] Wix error:', e); wixHasNext = false; }
        } else {
          wixHasNext = false;
        }
      }

      console.log('[loadMore] total more:', more.length);
      if (more.length > 0) {
        setBookings(prev => {
          const existingIds = new Set(prev.map(b => b.id));
          return [...prev, ...more.filter(b => !existingIds.has(b.id))];
        });
        loadedStartRef.current = newStart;
      }
      setHasMore(wixModeRef.current ? wixHasNext : more.length > 0);
    } catch (e) {
      console.error('[loadMore] unexpected error:', e);
    } finally {
      isLoadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [hasMore]);

  // Get unique service types from bookings
  const uniqueServiceTypes = useMemo(() => {
    const types = new Set<string>();
    bookings.forEach(booking => {
      if (booking.serviceType && booking.serviceType.trim()) {
        types.add(booking.serviceType);
      }
    });
    return Array.from(types).sort();
  }, [bookings]);

  // Get unique staff members from bookings
  const uniqueStaffMembers = useMemo(() => {
    const staff = new Set<string>();
    bookings.forEach(booking => {
      if (booking.staff && booking.staff.trim()) {
        staff.add(booking.staff);
      }
    });
    return Array.from(staff).sort();
  }, [bookings]);

  // Get unique resources from bookings
  const uniqueResources = useMemo(() => {
    const resources = new Set<string>();
    bookings.forEach(booking => {
      if (booking.resource && booking.resource.trim()) {
        resources.add(booking.resource);
      }
    });
    return Array.from(resources).sort();
  }, [bookings]);

  // Filter bookings
  const processedBookings = useMemo(() => {
    // First filter by search text
    let filtered = bookings.filter(booking => {
      const searchLower = searchText.toLowerCase();
      const clientName = booking.contact?.name?.toLowerCase() || '';
      const serviceType = booking.serviceType?.toLowerCase() || '';
      const address = booking.contact?.address?.toLowerCase() || '';
      const bookingId = (booking.bookingId || booking.id || '').toLowerCase();

      return clientName.includes(searchLower) ||
             serviceType.includes(searchLower) ||
             address.includes(searchLower) ||
             bookingId.includes(searchLower);
    });

    // Then apply active filter
    if (activeFilter !== 'all') {
      filtered = filtered.filter(booking => {
        switch (activeFilter) {
          case 'session-date-time':
            // Filter by date based on filterValue
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            // Parse the date string as local date (YYYY-MM-DD format)
            const [year, month, day] = booking.date.split('-').map(Number);
            const bookingDate = new Date(year, month - 1, day);
            const daysDiff = Math.round((bookingDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

            switch (filterValue) {
              case 'today':
                return daysDiff === 0;
              case 'tomorrow':
                return daysDiff === 1;
              case 'last-7-days':
                return daysDiff >= -7 && daysDiff <= 0;
              case 'last-14-days':
                return daysDiff >= -14 && daysDiff <= 0;
              case 'last-month':
                return daysDiff >= -30 && daysDiff <= 0;
              case 'custom':
                if (!customStartDate || !customEndDate) return true;
                const startDate = new Date(customStartDate);
                startDate.setHours(0, 0, 0, 0);
                const endDate = new Date(customEndDate);
                endDate.setHours(23, 59, 59, 999);
                return bookingDate >= startDate && bookingDate <= endDate;
              case 'all':
              default:
                return true;
            }

          case 'registration-date':
            // Filter by registration date based on filterValue
            const regToday = new Date();
            const registrationDate = new Date(booking.createdAt || booking.date); // Use createdAt if available, fallback to date
            const regDaysDiff = Math.floor((registrationDate.getTime() - regToday.getTime()) / (1000 * 60 * 60 * 24));

            switch (filterValue) {
              case 'last-7-days':
                return regDaysDiff >= -7 && regDaysDiff <= 0;
              case 'last-14-days':
                return regDaysDiff >= -14 && regDaysDiff <= 0;
              case 'last-month':
                return regDaysDiff >= -30 && regDaysDiff <= 0;
              case 'custom':
                if (!customStartDate || !customEndDate) return true;
                const regStartDate = new Date(customStartDate);
                regStartDate.setHours(0, 0, 0, 0);
                const regEndDate = new Date(customEndDate);
                regEndDate.setHours(23, 59, 59, 999);
                registrationDate.setHours(0, 0, 0, 0);
                return registrationDate >= regStartDate && registrationDate <= regEndDate;
              case 'all':
              default:
                return true;
            }

          case 'booking-status':
            // Filter by booking status
            if (filterValue === 'all') return true;
            // Wix reports CANCELED (one L); Firestore-mapped bookings use 'cancelled'.
            return booking.status?.toLowerCase().replace('canceled', 'cancelled') === filterValue.toLowerCase();

          case 'payment-status': {
            if (filterValue === 'all') return true;
            const status = booking.zohoPaymentStatus;
            if (filterValue === 'paid') return status === 'paid';
            if (filterValue === 'unpaid') return status === 'unpaid' || status === 'overdue' || status === 'partially_paid';
            if (filterValue === 'pending') return status === undefined || status === 'unknown' || status === 'draft' || status === 'sent' || status === 'viewed';
            return true;
          }

          case 'service-type':
          case 'service-name':
            // Filter by specific service type
            if (filterValue === 'all') return true;
            return booking.serviceType === filterValue;

          case 'staff-member':
            // Filter by specific staff member
            if (filterValue === 'all') return true;
            return booking.staff === filterValue;

          case 'resources':
            // Filter by specific resource
            if (filterValue === 'all') return true;
            return booking.resource === filterValue;

          case 'add-ons':
            // Filter by add-ons presence (using notes as proxy since addOns doesn't exist)
            if (filterValue === 'all') return true;
            if (filterValue === 'has-value') return booking.notes && booking.notes.trim() !== '';
            if (filterValue === 'no-value') return !booking.notes || booking.notes.trim() === '';
            return true;

          default:
            return true;
        }
      });
    }

    // Sort bookings
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'date-newest':
          return new Date(b.startTime).getTime() - new Date(a.startTime).getTime();
        case 'date-oldest':
          return new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
        case 'name-az':
          return (a.contact?.name || '').localeCompare(b.contact?.name || '');
        case 'name-za':
          return (b.contact?.name || '').localeCompare(a.contact?.name || '');
        default:
          return 0;
      }
    });

    return sorted;
  }, [bookings, searchText, activeFilter, filterValue, sortBy, customStartDate, customEndDate]);

  const displayedBookings = useMemo(
    () => processedBookings.slice(0, visibleCount),
    [processedBookings, visibleCount]
  );

  // Batch payment status — one Firebase call for all unfetched visible bookings.
  // Skips bookings already cached in Firestore (zohoPaymentStatus set by subscription).
  // Writes results back to Firestore so subsequent loads need 0 Zoho calls.
  useEffect(() => {
    const unfetched = displayedBookings.filter(b => !fetchedPaymentIds.current.has(b.id));
    if (!unfetched.length) return;
    unfetched.forEach(b => fetchedPaymentIds.current.add(b.id));

    const fromCache = unfetched.filter(b => b.zohoPaymentStatus !== undefined);
    if (fromCache.length) {
      setPaymentOverrides(prev => ({
        ...prev,
        ...Object.fromEntries(fromCache.map(b => [b.id, { paid: b.zohoPaid === true, paymentStatus: b.zohoPaymentStatus! }])),
      }));
    }

    const needsLookup = unfetched.filter(b => b.zohoPaymentStatus === undefined);
    if (!needsLookup.length) return;

    let cancelled = false;
    getBookingPaymentStatusBatch(needsLookup.map(b => b.id))
      .then(statuses => { if (!cancelled) setPaymentOverrides(prev => ({ ...prev, ...statuses })); })
      .catch(() => { /* silent */ });

    return () => { cancelled = true; };
  }, [displayedBookings]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'danger';
      default:
        return 'medium';
    }
  };

  // ion-badge's default (non `.ion-color`) :host rule reads --background/--color
  // directly, so a tone class is applied instead of Ionic's `color` prop (which
  // switches ion-badge to a :host(.ion-color) rule that ignores --background/--color).
  const BADGE_TONE_CLASS: Record<string, string> = {
    success: 'badgeSuccess',
    warning: 'badgeWarning',
    danger: 'badgeDanger',
    medium: 'badgeMedium',
  };

  // Helper function to format time from Date or string
  const formatTime = (startTime: string | Date): string => {
    const date = typeof startTime === 'string' ? new Date(startTime) : startTime;
    return date.toLocaleTimeString(intlLocale(), {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Helper function to format date
  const formatDate = (dateStr: string): string => {
    // Parse as local date to avoid timezone shifts
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString(intlLocale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Overlay positioning for morphing search (no FAB in modal, full width)
  const updateOverlayPosition = useCallback(() => {
    applySearchOverlayPosition(overlayRef.current, isSearchVisible);
  }, [isSearchVisible]);

  useEffect(() => {
    updateOverlayPosition();
    window.addEventListener('resize', updateOverlayPosition);
    return () => window.removeEventListener('resize', updateOverlayPosition);
  }, [updateOverlayPosition]);

  useEffect(() => {
    const id = window.setTimeout(() => updateOverlayPosition(), 30);
    return () => window.clearTimeout(id);
  }, [isSearchVisible, updateOverlayPosition]);

  // Keyboard tracking: when keyboard opens, add padding to the scroll container
  // so list items aren't hidden behind the keyboard, and move the search overlay up.
  useEffect(() => {
    const scrollContainerEl = scrollContainerRef.current;
    let didShowHandle: { remove: () => Promise<void> } | null = null;
    let didHideHandle: { remove: () => Promise<void> } | null = null;
    let bottomAnimRaf = 0;
    let currentBottom = 0;

    const setBottomOffset = (offset: number) => {
      const el = overlayRef.current;
      if (!el) return;
      if (offset > 0) {
        el.style.setProperty('bottom', `${offset}px`, 'important');
        el.style.removeProperty('transform');
      } else {
        el.style.removeProperty('bottom');
        el.style.removeProperty('transform');
      }
    };

    const animateBottomTo = (kbHeight: number) => {
      const targetBottom = kbHeight > 0 ? kbHeight + KEYBOARD_GAP_PX : 0;
      if (bottomAnimRaf) cancelAnimationFrame(bottomAnimRaf);

      const startBottom = currentBottom;
      const delta = targetBottom - startBottom;
      if (Math.abs(delta) < 1) {
        currentBottom = targetBottom;
        setBottomOffset(currentBottom);
        return;
      }

      const duration = targetBottom > startBottom ? 220 : 170;
      const startTime = performance.now();

      const step = (now: number) => {
        const t = Math.min((now - startTime) / duration, 1);
        const eased = 1 - Math.pow(1 - t, 3);
        currentBottom = startBottom + (delta * eased);
        setBottomOffset(currentBottom);

        if (t < 1) {
          bottomAnimRaf = requestAnimationFrame(step);
        } else {
          currentBottom = targetBottom;
          setBottomOffset(currentBottom);
        }
      };

      bottomAnimRaf = requestAnimationFrame(step);
    };

    const applyKeyboardOffset = (kbHeight: number) => {
      // Adjust scroll container so content is scrollable above the keyboard
      const grid = scrollContainerRef.current;
      if (grid) {
        if (kbHeight > 0) {
          grid.style.paddingBottom = `${kbHeight + 60}px`; // extra space for search bar
        } else {
          grid.style.removeProperty('padding-bottom');
        }
      }
      animateBottomTo(kbHeight);
    };

    // Capacitor Keyboard events (fire globally regardless of modal context)
    try {
      Keyboard.addListener('keyboardWillShow', (info) => {
        applyKeyboardOffset(info.keyboardHeight || 0);
      }).then(handle => { didShowHandle = handle; }).catch(() => { });

      Keyboard.addListener('keyboardDidShow', (info) => {
        applyKeyboardOffset(info.keyboardHeight || 0);
      }).catch(() => { });

      Keyboard.addListener('keyboardWillHide', () => {
        applyKeyboardOffset(0);
      }).then(handle => { didHideHandle = handle; }).catch(() => { });

      Keyboard.addListener('keyboardDidHide', () => {
        applyKeyboardOffset(0);
      }).catch(() => { });
    } catch { }

    // Fallback: visualViewport (in case Capacitor events don't fire)
    const vv = window.visualViewport;
    let vvRaf = 0;
    const handleViewportResize = () => {
      if (vvRaf) cancelAnimationFrame(vvRaf);
      vvRaf = requestAnimationFrame(() => {
        if (!vv) return;
        const offset = window.innerHeight - vv.height - vv.offsetTop;
        applyKeyboardOffset(offset > 50 ? offset : 0);
      });
    };
    if (vv) {
      vv.addEventListener('resize', handleViewportResize);
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') setTimeout(handleViewportResize, 300);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      const grid = scrollContainerEl;
      if (grid) grid.style.removeProperty('padding-bottom');
      const el = overlayRef.current;
      if (el) { el.style.removeProperty('transform'); el.style.removeProperty('bottom'); }
      if (bottomAnimRaf) cancelAnimationFrame(bottomAnimRaf);
      if (vvRaf) cancelAnimationFrame(vvRaf);
      if (vv) vv.removeEventListener('resize', handleViewportResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      try { didShowHandle?.remove(); } catch { }
      try { didHideHandle?.remove(); } catch { }
    };
  }, []);

  const handleToggleSearch = () => {
    if (isSearchVisible) {
      // Clear input immediately
      if (searchRef.current) {
        try { (searchRef.current as HTMLIonSearchbarElement).value = ''; } catch { }
      }
      setSearchText('');
      // Start closing animation
      setIsSearchClosing(true);
      setIsSearchVisible(false);
      setTimeout(() => {
        setIsSearchHidden(true);
        setIsSearchClosing(false);
      }, 220);

      // Blur input and hide keyboard
      try {
        const s = searchRef.current as HTMLIonSearchbarElement | null;
        const getter = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
        if (getter) {
          getter().then((inputEl: HTMLInputElement) => {
            try { inputEl.blur(); } catch { }
            try { Keyboard.hide(); } catch { }
          }).catch(() => { try { Keyboard.hide(); } catch { } });
        } else {
          try { Keyboard.hide(); } catch { }
        }
      } catch { }
    } else {
      setIsSearchHidden(false);
      setIsSearchVisible(true);
      // Focus input after expansion animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          try {
            const s = searchRef.current as HTMLIonSearchbarElement | null;
            const getter = s ? (s as unknown as { getInputElement?: () => Promise<HTMLInputElement> }).getInputElement : undefined;
            if (getter) {
              getter().then((inputEl: HTMLInputElement) => {
                try { inputEl.focus(); } catch { }
              }).catch(() => { });
            }
          } catch { }
        });
      });
    }
  };

  const applyProgress = useCallback((progress: number, smooth: boolean) => {
    let headerEl: HTMLElement | null = null;
    const modalHost = scrollContainerRef.current?.closest('ion-modal') as HTMLElement | null;
    if (modalHost) {
      headerEl = modalHost.querySelector<HTMLElement>('[data-header-title]');
    }
    if (!headerEl) {
      const contentHost = scrollContainerRef.current?.closest('ion-content') as HTMLElement | null;
      headerEl = contentHost?.querySelector<HTMLElement>('[data-header-title]') ?? document.querySelector<HTMLElement>('[data-header-title]');
    }

    if (smooth) {
      // Temporarily add a CSS transition for the snap
      if (headerEl) headerEl.style.transition = 'opacity 0.2s ease-out';
      if (titleCardRef.current) {
        titleCardRef.current.style.transition = 'opacity 0.2s ease-out, transform 0.2s ease-out';
      }
      // Remove the transition after it completes
      setTimeout(() => {
        if (headerEl) headerEl.style.transition = '';
        if (titleCardRef.current) titleCardRef.current.style.transition = '';
      }, 220);
    }

    headerEl?.style.setProperty('--title-opacity', String(progress));

    if (titleCardRef.current) {
      titleCardRef.current.style.setProperty('--content-opacity', String(1 - progress));
      titleCardRef.current.style.setProperty('--content-scale', `scale(${1 - (progress * 0.2)})`);
    }
  }, []);

  const handleStatusClick = useCallback(async (bookingId: string, payLabel: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!payLabel || payLabel === 'No Order') {
      setShowCreateInvoice(true);
      return;
    }
    setIsLookingUpInvoice(bookingId);
    let pdfOpened = false;
    try {
      console.log('[PDF] 1. getServiceOrder start', bookingId);
      const so = await getServiceOrder(bookingId);
      console.log('[PDF] 2. getServiceOrder result', so ? so.salesorder_number : 'null');
      if (!so) { setShowCreateInvoice(true); return; }

      const fn = httpsCallable(getFunctions(), 'zohoInvoicing');
      const isInvoiceStatus = payLabel === 'Paid' || payLabel === 'Sent' || payLabel === 'Free';

      if (isInvoiceStatus) {
        console.log('[PDF] 3. getZohoInvoices for', so.salesorder_number);
        const invoices = await getZohoInvoices({ search_text: so.salesorder_number });
        console.log('[PDF] 4. invoices found:', invoices.length);
        if (invoices.length > 0) {
          const title = tr('components.BookingsListModal.invoice', { v1: invoices[0].invoice_number ?? '' });
          console.log('[PDF] 5. opening modal, calling Firebase for invoiceId:', invoices[0].invoice_id);
          setPdfModalTitle(title);
          setPdfDataUrl(null);
          setIsPdfLoading(true);
          setShowPdfModal(true);
          pdfOpened = true;
          const result = await fn({ action: 'pdf', invoiceId: invoices[0].invoice_id });
          const data = result.data as { pdf?: string };
          console.log('[PDF] 6. Firebase returned pdf:', !!data?.pdf, 'b64 length:', data?.pdf?.length ?? 0);
          if (data?.pdf) setPdfDataUrl(`data:application/pdf;base64,${data.pdf}`);
          setIsPdfLoading(false);
        }
      } else {
        const title = tr('components.BookingsListModal.serviceOrder', { salesorder_number: so.salesorder_number });
        console.log('[PDF] 3b. opening modal, calling Firebase for salesOrderId:', so.salesorder_id);
        setPdfModalTitle(title);
        setPdfDataUrl(null);
        setIsPdfLoading(true);
        setShowPdfModal(true);
        pdfOpened = true;
        const result = await fn({ action: 'salesOrderPdf', salesOrderId: so.salesorder_id });
        const data = result.data as { pdf?: string };
        console.log('[PDF] 4b. Firebase returned pdf:', !!data?.pdf, 'b64 length:', data?.pdf?.length ?? 0);
        if (data?.pdf) setPdfDataUrl(`data:application/pdf;base64,${data.pdf}`);
        setIsPdfLoading(false);
      }
    } catch (err) {
      console.error('[PDF] ERROR in handleStatusClick', err);
      if (pdfOpened) setIsPdfLoading(false);
    } finally {
      setIsLookingUpInvoice(null);
    }
  }, [tr]);

  const handleScroll = useScrollSnap(scrollContainerRef, 66, applyProgress);

  const FILTER_LABELS: Record<string, string> = {
    'all': tr('components.BookingsListModal.allBookings'), 'session-date-time': tr('components.BookingsListModal.sessionDate'),
    'registration-date': tr('components.BookingsListModal.regDate'), 'service-type': tr('components.BookingsListModal.serviceType'),
    'booking-status': tr('components.BookingsListModal.status'), 'payment-status': tr('components.BookingsListModal.payment'),
    'service-name': tr('components.BookingsListModal.service'), 'staff-member': tr('components.BookingsListModal.staff'),
    'resources': tr('components.BookingsListModal.resources'), 'add-ons': tr('components.BookingsListModal.addOns'),
    'client-name': tr('components.BookingsListModal.client'), 'location': tr('components.BookingsListModal.location'),
  };

  const FILTER_VALUE_LABELS: Record<string, string> = {
    'all': 'All', 'today': tr('components.BookingsListModal.today'), 'tomorrow': tr('components.BookingsListModal.tomorrow'),
    'last-7-days': tr('components.BookingsListModal.last7Days'), 'last-14-days': tr('components.BookingsListModal.last14Days'),
    'last-month': tr('components.BookingsListModal.lastMonth'), 'custom': tr('components.BookingsListModal.custom'),
    'confirmed': tr('components.BookingsListModal.confirmed'), 'pending': tr('components.BookingsListModal.pending'),
    'completed': tr('components.BookingsListModal.completed'), 'cancelled': 'Cancelled',
    'paid': 'Paid', 'unpaid': tr('components.BookingsListModal.unpaid'),
    'has-value': tr('components.BookingsListModal.hasAddOns'), 'no-value': tr('components.BookingsListModal.noAddOns'),
  };

  const sel = (v: string) => filterValue === v;

  const filterValueActions = (() => {
    const all = { label: tr('components.BookingsListModal.all'), icon: listOutline, selected: sel('all'), handler: () => setFilterValue('all') };
    switch (activeFilter) {
      case 'session-date-time': return [
        all,
        { label: tr('components.BookingsListModal.today'), icon: calendarOutline, selected: sel('today'), handler: () => setFilterValue('today') },
        { label: tr('components.BookingsListModal.tomorrow'), icon: calendarOutline, selected: sel('tomorrow'), handler: () => setFilterValue('tomorrow') },
        { label: tr('components.BookingsListModal.last7Days'), icon: calendarOutline, selected: sel('last-7-days'), handler: () => setFilterValue('last-7-days') },
        { label: tr('components.BookingsListModal.last14Days'), icon: calendarOutline, selected: sel('last-14-days'), handler: () => setFilterValue('last-14-days') },
        { label: tr('components.BookingsListModal.lastMonth'), icon: calendarOutline, selected: sel('last-month'), handler: () => setFilterValue('last-month') },
        { label: tr('components.BookingsListModal.customRange'), icon: calendarOutline, selected: sel('custom'), handler: () => { setFilterValue('custom'); setTimeout(() => setShowCustomDatePopover(true), 300); } },
      ];
      case 'registration-date': return [
        all,
        { label: tr('components.BookingsListModal.last7Days'), icon: calendarOutline, selected: sel('last-7-days'), handler: () => setFilterValue('last-7-days') },
        { label: tr('components.BookingsListModal.last14Days'), icon: calendarOutline, selected: sel('last-14-days'), handler: () => setFilterValue('last-14-days') },
        { label: tr('components.BookingsListModal.lastMonth'), icon: calendarOutline, selected: sel('last-month'), handler: () => setFilterValue('last-month') },
        { label: tr('components.BookingsListModal.customRange'), icon: calendarOutline, selected: sel('custom'), handler: () => { setFilterValue('custom'); setTimeout(() => setShowCustomDatePopover(true), 300); } },
      ];
      case 'booking-status': return [
        all,
        { label: tr('components.BookingsListModal.confirmed'), icon: sparklesOutline, selected: sel('confirmed'), handler: () => setFilterValue('confirmed') },
        { label: tr('components.BookingsListModal.pending'), icon: sparklesOutline, selected: sel('pending'), handler: () => setFilterValue('pending') },
        { label: tr('components.BookingsListModal.completed'), icon: sparklesOutline, selected: sel('completed'), handler: () => setFilterValue('completed') },
        { label: tr('components.BookingsListModal.cancelled'), icon: sparklesOutline, selected: sel('cancelled'), handler: () => setFilterValue('cancelled') },
      ];
      case 'payment-status': return [
        all,
        { label: tr('components.BookingsListModal.paid'), icon: receiptOutline, selected: sel('paid'), handler: () => setFilterValue('paid') },
        { label: tr('components.BookingsListModal.unpaid'), icon: receiptOutline, selected: sel('unpaid'), handler: () => setFilterValue('unpaid') },
        { label: tr('components.BookingsListModal.pending'), icon: receiptOutline, selected: sel('pending'), handler: () => setFilterValue('pending') },
      ];
      case 'service-type': case 'service-name': return [
        all,
        ...uniqueServiceTypes.map(s => ({ label: s, icon: sparklesOutline, selected: sel(s), handler: () => setFilterValue(s) })),
      ];
      case 'staff-member': return [
        all,
        ...uniqueStaffMembers.map(s => ({ label: s, icon: personOutline, selected: sel(s), handler: () => setFilterValue(s) })),
      ];
      case 'resources': return [
        all,
        ...uniqueResources.map(r => ({ label: r, icon: listOutline, selected: sel(r), handler: () => setFilterValue(r) })),
      ];
      case 'add-ons': return [
        all,
        { label: tr('components.BookingsListModal.hasAddOns'), icon: listOutline, selected: sel('has-value'), handler: () => setFilterValue('has-value') },
        { label: tr('components.BookingsListModal.noAddOns'), icon: listOutline, selected: sel('no-value'), handler: () => setFilterValue('no-value') },
      ];
      default: return [all];
    }
  })();

  const inner = (
    <>
        {/* Portal target for the Filter/Sort PortalDropdowns below — a direct
            child of this fragment, so it lands as a direct child of
            whichever real wrapper (IonModal in modal mode, IonPage when
            asPage) hosts it, rather than nested inside a button row. */}
        <div ref={pickerPortalRef} className={styles.pickerPortalRoot} />
        <BasePageWithHeader
        title={tr('components.BookingsListModal.bookingsList')}
        toolbarClassName={styles.toolbar}
        smallTitleClassName={styles.smallTitle}
        titleHeaderClassName={styles.titleHeader}
        startButtons={
          <IonButton shape="round" className={styles.closeButton} onClick={handleClose}>
            <IonIcon icon={asPage ? chevronBack : closeOutline} />
          </IonButton>
        }
        endButtons={
          <IonButton
            shape="round"
            className={styles.searchButton}
            onClick={handleToggleSearch}
          >
            <IonIcon icon={isSearchVisible ? closeOutline : search} />
          </IonButton>
        }
      >
        <IonContent ref={ionContentRef} className={styles.content} onIonScroll={(e) => handleScroll(e.detail.scrollTop)}>
          <PositionedRefresher
            titleRef={titleCardRef}
            contentRef={scrollContainerRef}
            offset={15}
            anchor="bottom"
            id="bookings-refresher"
            className={styles.bookingsRefresher}
            pullMin={120}
            pullMax={200}
            pullFactor={0.6}
            disabled={refresherDisabled}
            onIonRefresh={(e) => {
              setIsLoading(true);
              setBookings([]);
              setVisibleCount(30);
              setPaymentOverrides({});
              fetchedPaymentIds.current.clear();
              const loadBookings = async () => {
                try {
                  // Reset window to default on pull-refresh
                  const d = new Date();
                  loadedStartRef.current = new Date(new Date().setDate(d.getDate() - PAGE_DAYS));
                  loadedEndRef.current = new Date(new Date().setDate(d.getDate() + PAGE_DAYS));
                  await fetchBookings();
                  setHasMore(true);
                } catch (error) {
                  console.error('Error refreshing bookings:', error);
                } finally {
                  e.detail.complete();
                }
              };
              loadBookings();
            }}
            style={{
              '--ion-color-step-250': 'var(--ion-background-color)',
              '--ion-color-step-200': 'var(--ion-background-color)',
              '--ion-background-color': 'transparent',
            } as React.CSSProperties}
          >
            <IonRefresherContent pullingIcon="bubbles" refreshingSpinner="bubbles" />
          </PositionedRefresher>

        {/* Inner scroll container (keeps SCSS unchanged) */}
        <IonGrid
          ref={scrollContainerRef}
          className={styles.bookingListContainer}
          onScroll={(e) => {
            const el = e.target as HTMLElement;
            const top = el.scrollTop || 0;
            // Only enable refresher when grid is truly at the top
            const atTop = top <= 5;
            if (atTop !== wasAtTop.current) {
              wasAtTop.current = atTop;
              setRefresherDisabled(!atTop);
            }
            if (!rafTick.current) {
              rafTick.current = true;
              requestAnimationFrame(() => {
                handleScroll(top);
                rafTick.current = false;
              });
            }
            // Reveal next 30 bookings or fetch more from API when near the bottom
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 150) {
              setVisibleCount(prev => {
                if (prev < processedBookings.length) return prev + 30;
                loadMore();
                return prev;
              });
            }
          }}
        >
          <IonRow ref={titleCardRef} className={styles.titleCardWrapper}>
            <IonCol>
              <TitleCard
                title={tr('components.BookingsListModal.bookingsList')}
                cardClassName={styles.bookingsTitleCard}
                titleContainerClassName={styles.titleContainer}
                titleClassName={styles.bookingsTitle}
              />
            </IonCol>
          </IonRow>

        {/* Filter Bar */}
        <div className={styles.filterBar}>
          <button
            ref={filterCatTriggerRef}
            className={`${styles.filterChip} ${activeFilter !== 'all' ? styles.filterChipActive : ''}`}
            onClick={() => setShowFilterCatMenu(v => !v)}
          >
            <IonIcon icon={funnelOutline} />
            <span>{FILTER_LABELS[activeFilter] ?? tr('components.BookingsListModal.filter')}</span>
            <IonIcon icon={chevronDownOutline} className={styles.chipChevron} />
          </button>

          {activeFilter !== 'all' && (
            <button
              ref={filterValTriggerRef}
              className={`${styles.filterChip} ${filterValue !== 'all' ? styles.filterChipActive : ''}`}
              onClick={() => setShowFilterValMenu(v => !v)}
            >
              <span>{FILTER_VALUE_LABELS[filterValue] ?? filterValue}</span>
              <IonIcon icon={chevronDownOutline} className={styles.chipChevron} />
            </button>
          )}

          <button
            ref={sortTriggerRef}
            className={styles.sortChip}
            onClick={() => setShowSortMenu(v => !v)}
          >
            <IonIcon icon={swapVertical} />
          </button>
        </div>

        <PortalDropdown
          isOpen={showFilterCatMenu}
          anchorRef={filterCatTriggerRef}
          portalRef={pickerPortalRef}
          onClose={() => setShowFilterCatMenu(false)}
          align="start"
          matchWidth={false}
          title={tr('components.BookingsListModal.filterBy')}
          options={[
            { label: tr('components.BookingsListModal.allBookings'), icon: listOutline, selected: activeFilter === 'all', handler: () => { setActiveFilter('all'); setFilterValue('all'); } },
            { label: tr('components.BookingsListModal.sessionDate'), icon: calendarOutline, selected: activeFilter === 'session-date-time', handler: () => { setActiveFilter('session-date-time'); setFilterValue('all'); } },
            { label: tr('components.BookingsListModal.registrationDate'), icon: calendarOutline, selected: activeFilter === 'registration-date', handler: () => { setActiveFilter('registration-date'); setFilterValue('all'); } },
            { label: tr('components.BookingsListModal.bookingStatus'), icon: sparklesOutline, selected: activeFilter === 'booking-status', handler: () => { setActiveFilter('booking-status'); setFilterValue('all'); } },
            { label: tr('components.BookingsListModal.paymentStatus'), icon: receiptOutline, selected: activeFilter === 'payment-status', handler: () => { setActiveFilter('payment-status'); setFilterValue('all'); } },
            { label: tr('components.BookingsListModal.serviceType'), icon: sparklesOutline, selected: activeFilter === 'service-type', handler: () => { setActiveFilter('service-type'); setFilterValue('all'); } },
            { label: tr('components.BookingsListModal.staffMember'), icon: personOutline, selected: activeFilter === 'staff-member', handler: () => { setActiveFilter('staff-member'); setFilterValue('all'); } },
          ]}
        />

        {activeFilter !== 'all' && (
          <PortalDropdown
            isOpen={showFilterValMenu}
            anchorRef={filterValTriggerRef}
            portalRef={pickerPortalRef}
            onClose={() => setShowFilterValMenu(false)}
            align="start"
            matchWidth={false}
            title={FILTER_LABELS[activeFilter]}
            options={filterValueActions}
          />
        )}

        <PortalDropdown
          isOpen={showSortMenu}
          anchorRef={sortTriggerRef}
          portalRef={pickerPortalRef}
          onClose={() => setShowSortMenu(false)}
          align="end"
          title={tr('components.BookingsListModal.sortBy')}
          options={[
            { label: tr('components.BookingsListModal.dateNewestFirst'), icon: calendarOutline, selected: sortBy === 'date-newest', handler: () => setSortBy('date-newest') },
            { label: tr('components.BookingsListModal.dateOldestFirst'), icon: calendarOutline, selected: sortBy === 'date-oldest', handler: () => setSortBy('date-oldest') },
            { label: tr('components.BookingsListModal.nameAZ'), icon: personOutline, selected: sortBy === 'name-az', handler: () => setSortBy('name-az') },
            { label: tr('components.BookingsListModal.nameZA'), icon: personOutline, selected: sortBy === 'name-za', handler: () => setSortBy('name-za') },
          ]}
        />

        {isLoading ? (
          <IonList className={styles.cardsContainer} lines="none">
            {Array.from({ length: 5 }).map((_, idx) => (
              <IonCard key={`skeleton-${idx}`} className={styles.bookingCard}>
                <IonCardContent>
                  <IonItem lines="none" className={styles.cardTopItem}>
                    <IonAvatar slot="start" className={styles.cardAvatar}>
                      <IonSkeletonText animated />
                    </IonAvatar>
                    <IonLabel>
                      <IonSkeletonText animated style={{ width: '55%', height: '16px' }} />
                    </IonLabel>
                    <IonSkeletonText animated style={{ width: '60px', height: '22px', borderRadius: '12px' }} slot="end" />
                  </IonItem>
                  <IonGrid className={styles.statsRow}>
                    <IonRow>
                      <IonCol className={styles.statCol}>
                        <IonSkeletonText animated style={{ width: '16px', height: '16px', borderRadius: '4px' }} />
                        <IonSkeletonText animated style={{ width: '60px', height: '13px', borderRadius: '4px' }} />
                        <IonSkeletonText animated style={{ width: '28px', height: '10px', borderRadius: '4px' }} />
                      </IonCol>
                      <IonCol className={styles.statCol}>
                        <IonSkeletonText animated style={{ width: '16px', height: '16px', borderRadius: '4px' }} />
                        <IonSkeletonText animated style={{ width: '48px', height: '13px', borderRadius: '4px' }} />
                        <IonSkeletonText animated style={{ width: '28px', height: '10px', borderRadius: '4px' }} />
                      </IonCol>
                      <IonCol className={styles.statCol}>
                        <IonSkeletonText animated style={{ width: '16px', height: '16px', borderRadius: '4px' }} />
                        <IonSkeletonText animated style={{ width: '56px', height: '13px', borderRadius: '4px' }} />
                        <IonSkeletonText animated style={{ width: '36px', height: '10px', borderRadius: '4px' }} />
                      </IonCol>
                    </IonRow>
                  </IonGrid>
                </IonCardContent>
              </IonCard>
            ))}
          </IonList>
        ) : error ? (
          <div className={styles.emptyState}>
            <IonText color="danger">
              <p>{error}</p>
            </IonText>
          </div>
        ) : (
          <IonList className={styles.cardsContainer} lines="none">
            {displayedBookings.map((booking) => {
              const payStatus = paymentOverrides[booking.id]?.paymentStatus ?? booking.zohoPaymentStatus;
              let payColor = 'medium', payLabel = 'No Order';
              if (payStatus === 'paid') { payColor = 'success'; payLabel = 'Paid'; }
              else if (payStatus === 'overdue') { payColor = 'danger'; payLabel = tr('components.BookingsListModal.overdue'); }
              else if (payStatus === 'unpaid') { payColor = 'warning'; payLabel = tr('components.BookingsListModal.unpaid'); }
              else if (payStatus === 'partially_paid') { payColor = 'warning'; payLabel = tr('components.BookingsListModal.partial'); }
              else if (payStatus === 'sent' || payStatus === 'viewed') { payColor = 'primary'; payLabel = 'Sent'; }
              else if (payStatus === 'draft') { payColor = 'medium'; payLabel = tr('components.BookingsListModal.draft'); }
              else if (payStatus === 'unknown' && booking.serviceType === 'Free Estimate') { payColor = 'tertiary'; payLabel = 'Free'; }

              const statusLower = booking.status.toLowerCase();
              const cardStatusClass =
                statusLower === 'confirmed' ? styles.cardConfirmed :
                statusLower === 'pending' ? styles.cardPending :
                (statusLower === 'cancelled' || statusLower === 'canceled') ? styles.cardCancelled : '';

              return (
                <IonCard
                  key={booking.id}
                  className={`${styles.bookingCard} ${cardStatusClass}`}
                  button
                  onClick={() => {
                    DataStorageService.storeData(`booking-${booking.id}`, booking);
                    DataStorageService.storeData('open-booking-details-modal', booking.id);
                  }}
                >
                  <IonCardContent>
                    <IonItem lines="none" className={styles.cardTopItem}>
                      <IonAvatar slot="start" className={styles.cardAvatar}>
                        <img
                          src={booking.contact?.avatarUrl ? proxyImage(booking.contact.avatarUrl) : getDefaultAvatar(booking.contact?.name || '')}
                          alt={booking.contact?.name || ''}
                          onError={(e) => { (e.target as HTMLImageElement).src = getDefaultAvatar(booking.contact?.name || ''); }}
                        />
                      </IonAvatar>
                      <IonLabel>
                        <h2 className={styles.cardName}>{booking.contact?.name || tr('components.BookingsListModal.unknownClient')}</h2>
                      </IonLabel>
                      <IonBadge className={`${styles.badge} ${styles[BADGE_TONE_CLASS[getStatusColor(booking.status)]]}`} slot="end">
                        {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                      </IonBadge>
                    </IonItem>

                    <IonText className={styles.serviceRow}>
                      <IonIcon icon={sparklesOutline} className={styles.serviceRowIcon} />
                      {booking.serviceType || tr('components.BookingsListModal.service')}
                      {booking.staff && (
                        <>
                          <span className={styles.serviceRowDot}>·</span>
                          <IonIcon icon={personOutline} className={styles.serviceRowIcon} />
                          {booking.staff}
                        </>
                      )}
                    </IonText>

                    <IonGrid className={styles.statsRow}>
                      <IonRow>
                        <IonCol className={styles.statCol}>
                          <IonIcon icon={calendar} className={`${styles.statIcon} ${styles.statIconBlue}`} />
                          <IonText><p className={styles.statValue}>{formatDate(booking.date)}</p></IonText>
                          <IonText><p className={styles.statMeta}>{tr('components.BookingsListModal.date')}</p></IonText>
                        </IonCol>
                        <IonCol className={styles.statCol}>
                          <IonIcon icon={time} className={`${styles.statIcon} ${styles.statIconBlue}`} />
                          <IonText><p className={styles.statValue}>{formatTime(booking.startTime)}</p></IonText>
                          <IonText><p className={styles.statMeta}>{tr('components.BookingsListModal.time')}</p></IonText>
                        </IonCol>
                        <IonCol
                          className={`${styles.statCol} ${styles.statColLast} ${styles.statColClickable}`}
                          onClick={(e) => handleStatusClick(booking.id, payLabel, e)}
                        >
                          {isLookingUpInvoice === booking.id
                            ? <IonSpinner name="dots" className={styles.statIcon} />
                            : <IonIcon icon={receiptOutline} className={`${styles.statIcon} ${styles[`statIconPay${payColor.charAt(0).toUpperCase() + payColor.slice(1)}`]}`} />
                          }
                          <IonText><p className={`${styles.statValue} ${styles[`payText${payColor.charAt(0).toUpperCase() + payColor.slice(1)}`]}`}>
                            {translateDynamic(payLabel)}
                          </p></IonText>
                          <IonText><p className={styles.statMeta}>{tr('components.BookingsListModal.status')}</p></IonText>
                        </IonCol>
                      </IonRow>
                    </IonGrid>

                    {booking.contact?.address && (
                      <IonItem lines="none" className={styles.cardFooterItem}>
                        <IonLabel className={styles.footerAddressLabel}>
                          <IonIcon icon={locationOutline} className={styles.footerPinIcon} />
                          <span className={styles.footerAddress}>{booking.contact.address}</span>
                        </IonLabel>
                      </IonItem>
                    )}
                  </IonCardContent>
                </IonCard>
              );
            })}
          </IonList>
        )}


        {!isLoading && !error && displayedBookings.length === 0 && (
          <div className={styles.emptyState}>
            <IonText color="medium">
              <p>{tr('components.BookingsListModal.noBookingsFound')}</p>
            </IonText>
          </div>
        )}

        {isLoadingMore && (
          <div className={styles.loadMoreSpinner}>
            <IonSpinner name="bubbles" color="primary" />
          </div>
        )}

        {/* Spacer when search is visible so last items aren't hidden */}
        {isSearchVisible && <div className={styles.searchBarSpacer} />}
        </IonGrid>
        </IonContent>

        {/* Morphing search overlay (outside IonContent, inside BasePageWithHeader) */}
        <IonFab
          ref={el => { overlayRef.current = el; }}
          className={`${styles.searchMorph} ${isSearchVisible ? styles.searchMorphExpanded : ''} ${isSearchClosing ? styles.searchMorphClosing : ''} ${isSearchHidden ? styles.hidden : ''}`}
          aria-hidden={!isSearchVisible}
        >
          <IonSearchbar
            ref={searchRef}
            value={searchText}
            debounce={50}
            onIonInput={e => setSearchText(e.detail.value ?? '')}
            onIonClear={() => setSearchText('')}
            placeholder={tr('components.BookingsListModal.searchBookings', { length: bookings.length })}
            animated
            showCancelButton="never"
            showClearButton={searchText ? 'always' : 'never'}
            className={`${styles.searchBar} ${styles.floatingSearchBarInline} ${isSearchVisible ? styles.floatingSearchBarInlineExpanded : ''}`}
          />
        </IonFab>
      </BasePageWithHeader>

      {/* Custom Date Range Popover */}
      <IonPopover
        isOpen={showCustomDatePopover}
        onDidDismiss={() => {
          console.log('Custom date popover dismissed');
          setShowCustomDatePopover(false);
        }}
        className={styles.customDatePopover}
        backdropDismiss={true}
      >
        <div className={styles.popoverContent}>
          <div className={styles.popoverHeader}>
            <h3>{tr('components.BookingsListModal.selectDateRange')}</h3>
            <IonButton fill="clear" size="small" onClick={() => setShowCustomDatePopover(false)}>
              <IonIcon icon={close} />
            </IonButton>
          </div>

          <div className={styles.datePickerContainer}>
            <div className={styles.datePickerSection}>
              <IonLabel className={styles.dateLabel}>{tr('components.BookingsListModal.startDate')}</IonLabel>
              <input
                type="date"
                value={customStartDate?.split('T')[0] || ''}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className={styles.nativeDatePicker}
                aria-label={tr('components.BookingsListModal.startDate')}
              />
            </div>

            <div className={styles.datePickerSection}>
              <IonLabel className={styles.dateLabel}>{tr('components.BookingsListModal.endDate')}</IonLabel>
              <input
                type="date"
                value={customEndDate?.split('T')[0] || ''}
                onChange={(e) => setCustomEndDate(e.target.value)}
                min={customStartDate?.split('T')[0] || ''}
                className={styles.nativeDatePicker}
                aria-label={tr('components.BookingsListModal.endDate')}
              />
            </div>
          </div>

          <div className={styles.popoverActions}>
            <IonButton
              fill="clear"
              color="primary"
              onClick={() => setShowCustomDatePopover(false)}
              disabled={!customStartDate || !customEndDate}
            >{tr('components.BookingsListModal.applyDateRange')}
            </IonButton>
          </div>
        </div>
      </IonPopover>

      <IonModal
        isOpen={showCreateInvoice}
        onDidDismiss={() => setShowCreateInvoice(false)}
        handle={false}
      >
        {showCreateInvoice && <CreateInvoice onClose={() => setShowCreateInvoice(false)} />}
      </IonModal>

      <IonModal
        isOpen={showPdfModal}
        onDidDismiss={() => { setShowPdfModal(false); setPdfDataUrl(null); setIsPdfFullscreen(false); }}
        handle={false}
        className={`${styles.pdfModalIon} ${isPdfFullscreen ? styles.pdfModalFullscreen : ''}`}
      >
        {showPdfModal && (
          <div className={styles.pdfModal}>
            <div className={styles.pdfModalHeader}>
              <span className={styles.pdfModalTitle}>{pdfModalTitle}</span>
              <IonButton fill="clear" shape="round" onClick={() => { setShowPdfModal(false); setPdfDataUrl(null); setIsPdfFullscreen(false); }}>
                <IonIcon icon={closeOutline} />
              </IonButton>
            </div>
            <div className={styles.pdfModalContent}>
              <PdfViewerMobile dataUrl={pdfDataUrl} isLoading={isPdfLoading} fileName={pdfModalTitle} onFullscreenChange={setIsPdfFullscreen} />
            </div>
          </div>
        )}
      </IonModal>
    </>
  );

  return asPage
    ? <IonPage className={styles.page}>{inner}</IonPage>
    : <div className={styles.modalWrapper}>{inner}</div>;
};

export default BookingsListModal;